import { AppBar, Tab, Tabs } from '@mui/material';
import { startCase } from 'lodash';
import React, { useContext } from 'react';
import TitleBar from 'components/TitleBar/TitleBar';
import { Link, useMatch } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { RateTab } from 'api/consumablesApi';
import EquipmentRateCardHomePage from './EquipmentSection/EquipmentRateCardHomePage';
import ConsumableRateCardHomePage from './ConsumableSection/ConsumableRateCardHomePage';
import MarginSummaryHomePage from './MarginSummarySection/MarginSummaryHomePage';
import { shouldShowProIcon, userHasFeatureAccess } from 'models/Role';
import { FeatureFlag } from 'api/organizationApi';
import UserContext from 'state/UserContext';
import FeatureFlagView from './Utils/FeatureFlagView';
import LabourRateCardHomePage from './LabourSection/LabourRateCardHomePage';
import ProFeatureIcon from 'views/ProFeatureIcon';
import OtherExpensesMarginCardHomePage from './OtherExpensesSection/OtherExpensesMarginCardHomePage';

const RateCard = () => {
  const { user } = useContext(UserContext);
  // Match for tab routes
  const matchTab = useMatch('/rate-card/:tab');
  // Match for detail routes
  const matchConsumable = useMatch('/rate-card/consumables/:id');
  const matchEquipment = useMatch('/rate-card/equipment/:id');
  const matchLabour = useMatch('/rate-card/labour/:id');
  const matchOtherExpenses = useMatch('/rate-card/other-expenses/:id');

  const breadcrumbs: Array<{
    icon?: JSX.Element;
    path?: string;
    name?: string;
  }> = [
    {
      icon: <HomeIcon />,
      path: '/',
    },
    {
      name: 'Rate Card',
      icon: <LocalOfferIcon />,
    },
  ];

  if (matchConsumable) {
    breadcrumbs.push({
      name: RateTab.Consumables,
      path: '/rate-card/consumables',
    });
  } else if (matchEquipment) {
    breadcrumbs.push({ name: RateTab.Equipment, path: '/rate-card/equipment' });
  } else if (matchLabour) {
    breadcrumbs.push({ name: RateTab.Labour, path: '/rate-card/labour' });
  } else if (matchOtherExpenses) {
    breadcrumbs.push({
      name: RateTab.OtherExpenses,
      path: '/rate-card/other-expenses',
    });
  } else if (matchTab) {
    breadcrumbs.push({
      name: startCase(matchTab.params.tab),
      path: `/rate-card/${matchTab.params.tab}`,
    });
  } else {
    breadcrumbs.push({
      name: RateTab.Equipment,
      path: '/rate-card/equipment',
    });
  }

  // Determine selected tab
  const tabValue: RateTab =
    (matchTab?.params?.tab &&
    (Object.values(RateTab) as string[]).includes(matchTab.params.tab)
      ? (matchTab.params.tab as RateTab)
      : undefined) ||
    (matchConsumable &&
      userHasFeatureAccess(user, FeatureFlag.RATE_CARD_CONSUMABLE) &&
      RateTab.Consumables) ||
    (matchEquipment &&
      userHasFeatureAccess(user, FeatureFlag.RATE_CARD_EQUIPMENT) &&
      RateTab.Equipment) ||
    (matchLabour &&
      userHasFeatureAccess(user, FeatureFlag.RATE_CARD_LABOUR_RATE) &&
      RateTab.Labour) ||
    (matchOtherExpenses &&
      userHasFeatureAccess(user, FeatureFlag.RATE_CARD_OTHER_EXPENSES) &&
      RateTab.OtherExpenses) ||
    RateTab.Equipment;

  return (
    <>
      <TitleBar breadcrumbs={breadcrumbs} title="Rate Card" />
      <AppBar position="static" color="inherit" elevation={1}>
        <Tabs
          variant={'standard'}
          value={tabValue}
          indicatorColor="primary"
          textColor="primary">
          <Tab
            icon={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                {shouldShowProIcon(user, FeatureFlag.RATE_CARD_LABOUR_RATE) && (
                  <ProFeatureIcon />
                )}
                <span style={{ marginLeft: 0 }}>Labour</span>
              </span>
            }
            value={RateTab.Labour}
            component={Link}
            to="labour"
          />
          <Tab
            icon={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                {shouldShowProIcon(user, FeatureFlag.RATE_CARD_EQUIPMENT) && (
                  <ProFeatureIcon />
                )}
                <span style={{ marginLeft: 0 }}>Equipment</span>
              </span>
            }
            value={RateTab.Equipment}
            component={Link}
            to="equipment"
          />
          <Tab
            icon={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                {shouldShowProIcon(user, FeatureFlag.RATE_CARD_CONSUMABLE) && (
                  <ProFeatureIcon />
                )}
                <span style={{ marginLeft: 0 }}>Consumables</span>
              </span>
            }
            value={RateTab.Consumables}
            component={Link}
            to="consumables"
          />
          <Tab
            icon={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                {shouldShowProIcon(
                  user,
                  FeatureFlag.RATE_CARD_OTHER_EXPENSES
                ) && <ProFeatureIcon />}
                <span style={{ marginLeft: 0 }}>Other Expenses</span>
              </span>
            }
            value={RateTab.OtherExpenses}
            component={Link}
            to="other-expenses"
          />

          <Tab
            icon={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                {shouldShowProIcon(user, FeatureFlag.RATE_MARGIN_SUMMARY) && (
                  <ProFeatureIcon />
                )}
                <span style={{ marginLeft: 0 }}>Margin Summary</span>
              </span>
            }
            value={RateTab.MarginSummary}
            component={Link}
            to="margin-summary"
          />
        </Tabs>
      </AppBar>
      <div>
        <FeatureFlagView
          user={user}
          specialFeature={FeatureFlag.RATE_CARD_LABOUR_RATE}
          path="labour"
          component={<LabourRateCardHomePage />}
        />
        <FeatureFlagView
          user={user}
          specialFeature={FeatureFlag.RATE_CARD_CONSUMABLE}
          path="consumables"
          component={<ConsumableRateCardHomePage />}
        />
        <FeatureFlagView
          user={user}
          specialFeature={FeatureFlag.RATE_CARD_EQUIPMENT}
          path="equipment"
          component={<EquipmentRateCardHomePage />}
        />
        <FeatureFlagView
          user={user}
          specialFeature={FeatureFlag.RATE_CARD_OTHER_EXPENSES}
          path="other-expenses"
          component={<OtherExpensesMarginCardHomePage />}
        />
        <FeatureFlagView
          user={user}
          specialFeature={FeatureFlag.RATE_MARGIN_SUMMARY}
          path="margin-summary"
          component={<MarginSummaryHomePage />}
        />
      </div>
    </>
  );
};

export default RateCard;
